import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ComputerDesktopIcon,
  UserIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import '../styles/DetailView.css';
import '../styles/SystemUsersPanel.css';
import DetailCrumb from './DetailCrumb';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-ES');
}

const Field = ({ label, value, span = false }) => (
  <div className="sf2-field" style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label>{label}</label>
    <div className="v">{value || '—'}</div>
  </div>
);

function SystemUsersPanel({ record, companyId, onClose, getUsers, entityLabel = 'Servidor' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inventory, setInventory] = useState(null);
  const getUsersRef = useRef(getUsers);

  useEffect(() => {
    getUsersRef.current = getUsers;
  }, [getUsers]);

  const fetchInventory = useCallback(async () => {
    if (!record?.id || !companyId || !getUsersRef.current) return;
    setLoading(true);
    setError('');
    try {
      const response = await getUsersRef.current(record.id, companyId);
      setInventory(response?.data || null);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el inventario de usuarios');
      setInventory(null);
    } finally {
      setLoading(false);
    }
  }, [record?.id, companyId]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const payload = inventory?.data && typeof inventory.data === 'object' ? inventory.data : {};
  const validatedUser = payload.validated_user || null;
  const domain = payload.domain || {};
  const currentSessions = Array.isArray(payload.current_sessions) ? payload.current_sessions : [];
  const localUsers = Array.isArray(payload.local_users) ? payload.local_users : [];
  const historicalLogons = Array.isArray(payload.historical_logons) ? payload.historical_logons : [];
  const warnings = Array.isArray(payload.warnings) ? payload.warnings : [];
  const collector = payload.collector || {};

  const headerSummary = useMemo(() => {
    if (validatedUser?.raw) return validatedUser.raw;
    if (validatedUser?.username && validatedUser?.domain) return `${validatedUser.domain}\\${validatedUser.username}`;
    if (validatedUser?.username) return validatedUser.username;
    return 'Sin usuario validado';
  }, [validatedUser]);

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon /> {entityLabel}
        </button>
        <DetailCrumb label={record?.name} />
        <div className="dv-topbar-spacer" />
      </div>

      <div className="dv-body">
        <div className="dv-main">
          <div className="sup-header">
            <div className="sup-header-icon-wrap">
              <UserIcon className="sup-header-icon" />
            </div>
            <div>
              <div className="dv-record-title sup-record-title">{record?.name || entityLabel}</div>
              <div className="sup-record-subtitle">{headerSummary}</div>
            </div>
          </div>

          {loading && (
            <div className="dv-section sup-message-muted">Cargando inventario de usuarios…</div>
          )}

          {!loading && error && (
            <div className="dv-section sup-message-error">{error}</div>
          )}

          {!loading && !error && !inventory && (
            <div className="dv-section sup-message-muted">
              El agente todavía no ha enviado inventario de usuarios para este equipo.
            </div>
          )}

          {!loading && !error && inventory && (
            <div className="sf2-grid sf2-grid--2">
              {/* 1 · Equipo y captura */}
              <div className="sf2-card">
                <h3>
                  Equipo y captura
                  <ComputerDesktopIcon style={{ width: 16, height: 16, opacity: 0.7 }} />
                </h3>
                <div className="sf2-fields" style={{ '--n': 2 }}>
                  <Field label="Capturado" value={formatDate(inventory.inventory_date || payload.inventory_date)} />
                  <Field label="Tomado por" value={inventory.taken_by} />
                  <Field label="Plataforma" value={collector.platform} />
                  <Field label="Dominio" value={domain.name || (domain.part_of_domain ? 'Sí' : 'No')} />
                </div>
              </div>

              {/* 2 · Usuario validado */}
              <div className="sf2-card">
                <h3>
                  Usuario validado
                  <UserIcon style={{ width: 16, height: 16, opacity: 0.7 }} />
                </h3>
                {validatedUser ? (
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <Field label="Usuario" value={validatedUser.username} />
                    <Field label="Dominio" value={validatedUser.domain} />
                    <Field label="Origen" value={validatedUser.source} span />
                  </div>
                ) : (
                  <div className="sf2-hint">No se detectó usuario validado.</div>
                )}
              </div>

              {/* 3 · Sesiones actuales */}
              <div className="sf2-card">
                <h3>
                  Sesiones actuales
                  <ClockIcon style={{ width: 16, height: 16, opacity: 0.7 }} />
                  {currentSessions.length > 0 && (
                    <span className="sf2-pill sf2-pill--info">{currentSessions.length}</span>
                  )}
                </h3>
                {currentSessions.length === 0 && (
                  <div className="sf2-hint">No hay sesiones activas detectadas.</div>
                )}
                <div style={{ display: 'grid', gap: 8 }}>
                  {currentSessions.map((session, index) => (
                    <div key={`${session.username || 'session'}-${index}`} className="sup-item-card">
                      <div className="sup-item-card-header">
                        <strong>{session.username || 'Sin usuario'}</strong>
                        <span className="sf2-pill sf2-pill--ok">{session.state || '—'}</span>
                      </div>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        <Field label="Sesión" value={session.session_name || session.session_id} />
                        <Field label="Inicio" value={formatDate(session.logon_time)} />
                        <Field label="Inactiva" value={session.idle_time} />
                        <Field label="Origen" value={session.source || session.raw} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 4 · Usuarios del sistema */}
              <div className="sf2-card">
                <h3>
                  Usuarios del sistema
                  {localUsers.length > 0 && (
                    <span className="sf2-pill sf2-pill--info">{localUsers.length}</span>
                  )}
                </h3>
                {localUsers.length === 0 ? (
                  <div className="sf2-hint">No se detectaron usuarios locales.</div>
                ) : (
                  <div className="dv-table-wrap">
                    <table className="dv-table">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th style={{ width: 70 }}>Activo</th>
                          <th>Último acceso</th>
                          <th>Perfil</th>
                        </tr>
                      </thead>
                      <tbody>
                        {localUsers.map((user, index) => (
                          <tr key={`${user.name || 'user'}-${index}`}>
                            <td>{user.name || '—'}</td>
                            <td>
                              {typeof user.enabled === 'boolean' ? (
                                <span className={`sf2-pill sf2-pill--${user.enabled ? 'ok' : 'wa'}`}>
                                  {user.enabled ? 'Sí' : 'No'}
                                </span>
                              ) : '—'}
                            </td>
                            <td>{formatDate(user.last_logon)}</td>
                            <td>{user.full_name || user.home || user.shell || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* 5 · Accesos recientes */}
              <div className="sf2-card">
                <h3>
                  Accesos recientes
                  {historicalLogons.length > 0 && (
                    <span className="sf2-pill sf2-pill--info">{historicalLogons.length}</span>
                  )}
                </h3>
                {historicalLogons.length === 0 ? (
                  <div className="sf2-hint">No hay histórico reciente disponible.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {historicalLogons.map((item, index) => (
                      <div key={`logon-${index}`} className="sup-item-card">
                        <div className="sup-item-card-header">
                          <strong>{item.username || item.raw || 'Acceso detectado'}</strong>
                          <span className="sup-item-meta">{formatDate(item.date)}</span>
                        </div>
                        <div className="sup-item-detail">
                          {item.domain ? <span><strong>Dominio:</strong> {item.domain} · </span> : null}
                          {item.logon_type ? <span><strong>Tipo:</strong> {item.logon_type}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 6 · Avisos del agente */}
              <div className="sf2-card">
                <h3>
                  Avisos del agente
                  {warnings.length > 0 && (
                    <span className="sf2-pill sf2-pill--wa">{warnings.length}</span>
                  )}
                </h3>
                {warnings.length === 0 ? (
                  <div className="sf2-hint">Sin avisos.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {warnings.map((warning, index) => (
                      <div key={`warning-${index}`} className="sup-warning-card">
                        <ExclamationTriangleIcon className="sup-warning-icon" />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemUsersPanel;
